/**
 * **游戏画面（没有卡牌的那部分）** —— 菜单**唯一**的背景。
 *
 * ## 为什么菜单不要自己画背景
 *
 * 评审：「这些 UI 界面都不要自己绘制背景，直接让一个没有任何卡牌的游戏画面
 * 作为背景，开始游戏后卡牌再进场就能实现无缝转场了。」
 *
 * 所以菜单下面垫的不是"一块给菜单用的背景"，而是**游戏画面本身**：
 * 同一个标量场、同一个半调、同一条濒死层。开局时发生的事只有一件 ——
 * **卡牌进场**。背景一个像素都不用重新初始化，也就不存在"切场景"那一帧。
 *
 * ⚠️ 由此推出一条纪律：**任何铺满全屏的菜单装饰都是 bug。**
 * 压暗层、专属底色、背景渐变 —— 它们都会在开局那一刻整体消失，
 * 那一瞬间就是一次肉眼可见的跳变。不透明面板（0.94 底）不会，
 * 因为面板是"浮在上面的纸片"，本来就该在游戏里继续存在。
 *
 * ## 层序（评审定死的顺序里属于背景的那一段）
 *
 * ```
 *   卡牌占地(mask)  →  背景（挖好洞）  →  濒死
 *   ─────────────────────────────────────────  以上是这个文件
 *   外发光  →  卡牌                              以上是游戏屏幕（下一步）
 * ```
 *
 * ## 为什么占地遮罩现在就要接上
 *
 * 它现在是**空的**（没有卡牌），但先接上有一条实在的好处：
 * 开局时"背景不挖洞"和"卡牌开始挖洞"之间**不需要换管线** ——
 * 变的只是喂给 `update()` 的卡牌列表。留到下一步再接线的话，
 * 那一步就还得同时改背景的遮罩来源，而那正是最容易把洞挖错的地方。
 */

import { Container, type Renderer, type Ticker } from 'pixi.js';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '../app';
import { Background, quadOf, type Viewport } from '../bg';
import { faceSize, type CardView } from '../card';
import { CrackMask } from '../dying/cracks';
import { CardFootprint } from '../cardFootprint';
import { DyingLayer } from '../dying';
import { DYING_GROW_TO, REVEAL_CARD_HEIGHT } from './drawReveal';

export interface GameBackdropOptions {
  readonly renderer: Renderer;
  readonly width?: number;
  readonly height?: number;
  readonly seed?: number;
}

export class GameBackdrop {
  /** 加到场景里的根。⚠️ 卡牌要加在它**后面**（更上面） */
  readonly view: Container;

  readonly footprint: CardFootprint;
  readonly background: Background;
  readonly dying: DyingLayer;
  readonly cracks: CrackMask;

  readonly #renderer: Renderer;

  constructor(options: GameBackdropOptions) {
    const width = options.width ?? LOGICAL_WIDTH;
    const height = options.height ?? LOGICAL_HEIGHT;

    this.#renderer = options.renderer;

    this.view = new Container();
    this.view.label = 'game-backdrop';

    // ① 卡牌占地。现在没有卡牌 → 遮罩是空的 → 背景一个洞都不挖
    this.footprint = new CardFootprint(options.renderer, { width, height });

    // ② 背景：标量场 + 半调。挖洞的来源**现在就是**真的那块占地遮罩
    // ⚠️ 这里**不撒任何初始扰动**：背景的"底"是标量场的初始状态
    //（alexharri 那套渐变背景，每一帧都在动），扰动只在事件发生时出现
    this.background = new Background({
      width,
      height,
      mask: this.footprint.mask,
      // ⚠️ 降分辨率那一层要用渲染器（场先画进小 FBO 再放大，见 `FIELD_PIXEL_EXPONENT`）
      renderer: this.#renderer,
    });

    // ③ 濒死裂缝：从中心放射出去的碎裂线条，**改的是背景的场**
    //    ⚠️ 它在**卡牌下面、背景的场之上** —— 它不是画上去的线，是"哪里不许有场"
    //    （见 `cracks.ts` 的文件头）
    this.cracks = new CrackMask({ renderer: options.renderer, width, height });
    this.background.setCrack(this.cracks.mask, 0);

    /**
     * ④ 濒死层。未开局时 `isDying` 恒为假，所以它从第一帧起就是关着的。
     *
     * ⚠️ **剪影按"放大之后的尺寸"烘**（评审：「若辉光剪影等预渲染内容对不上，
     *    那就在预渲染时按照放大后的尺寸进行渲染」）：那张牌在濒死期间一直是
     *    `DYING_GROW_TO` 倍大，辉光/阴影围着的就是它 —— 按原尺寸烘的话，
     *    停下来的时候牌会明显大出光晕一圈。
     */
    this.dying = new DyingLayer({
      renderer: options.renderer,
      width,
      height,
      card: faceSize(REVEAL_CARD_HEIGHT * DYING_GROW_TO),
    });
    this.dying.end();

    this.view.addChild(this.background.view, this.dying.view);
  }

  /**
   * 未开局时 `isDying` 必须为假 —— 这里是它**唯一**的落点。
   *
   * 评审：「未开始游戏时 isFinal、isDying 总是为假」。所以菜单从建出来到
   * 开局之前，一次都不会调到 `setDying(true)`。
   *
   * ## ⚠️ 这里是**边沿触发**，不是电平触发 —— 而且必须是这样
   *
   * 调用方（`ui/screenHost.ts`）每帧按 `energy.isDying` 报一次，所以
   * `active` 在整段濒死里一直是 `true`。第一版直接把它转给
   * `dying.begin()` / `cracks.begin()`，于是**每一帧都重新开始一次**：
   * 濒死层那个包络永远停在起点，裂缝的 `#age` 永远归零 ——
   * 后果是"进入濒死时什么特效都看不见"，而 `begin()` 本身不报错、
   * 单测也看不出来（它们直接调 `begin()`，那条路是对的）。
   *
   * 评审报的就是这个：「dying.html 中的背景裂痕效果你没实现」——
   * 那一页是手动调一次 `begin()`，所以有；游戏里每帧调一次，所以没有。
   */
  setDying(active: boolean): void {
    if (active === this.#dying) return;
    this.#dying = active;
    // ★ 背景自己那一份"濒死"：场的播放速度要翻倍（评审）
    this.background.setDying(active);
    if (active) {
      this.dying.begin();
      this.cracks.begin();
    } else {
      this.dying.end();
      this.cracks.end();
    }
  }

  #dying = false;

  /**
   * 现在在不在濒死。
   *
   * ⚠️ 读的是**这一层记的那个开关**（`#dying`），不是 `DyingLayer.active` ——
   * 后者还会被一次性的 `pulse()` 顶成真（那是"亮一下"的语义，不是"在濒死"）。
   */
  get isDying(): boolean {
    return this.#dying;
  }

  /**
   * **音乐走到下一个小节了。** 一屏一份，菜单里也在响。
   *
   * 两件事，都是"跟着拍子走"，所以都从这里进、都只吃一个强度：
   *
   * 1. **标量场**：从画面正中向外推一圈脉冲（`strength` 就是它的轻重）
   * 2. **濒死层**：正在濒死时敲一下重音
   *
   * ⚠️ `strength` 是 0..1 的系数，由调用方（`ui/screenHost.ts`）按
   * "乐句头 / 其余小节"算好 —— 渲染层不该认识"乐句"这个概念。
   *
   * ⚠️ 濒死那一支**必须判 `isDying`**。`DyingLayer` 有两条入口：
   * `pulse()` 会把 `target` 顶到 1（= 把濒死层打开），`barPulse()` 不会。
   * 走 `barPulse()` 正是为了这个 —— 菜单里每小节敲一下，绝不能把
   * 濒死层敲出来。
   */
  barTick(strength: number): void {
    // ★ **Bar 那一下整体只在濒死期间生效**（评审两次强调）：
    //   「Bar 触发的扰动是仅在濒死期间生效」+「Bar 泛红是 Bar 扰动附带的，
    //   应当一起仅在濒死期间触发，而不是分别做两个监听」。
    //
    //   所以**一个判断罩住三件事**：涟漪、泛红、濒死层的重音。
    //   分成两个判断（涟漪判濒死、泛红不判）是错的 —— 结果就是平常每小节
    //   背景都会红一下，而那一圈涟漪却不出来，两者对不上。
    if (!this.dying.active) return;
    this.background.pulseFromCenter(strength);
    this.background.barFlash();
    this.dying.barPulse(strength);
  }

  /**
   * **当前的情绪**（energy 决定脸色）。评审：越高越亮越橙、越低越暗越蓝。
   *
   * 宿主每帧把 `energy` 报进来 —— 它同时影响色调和那层的时间倍率，
   * 两件事都在 `Background.setEnergy` 里，见那个方法。
   */
  setEnergy(energy: number): void {
    this.background.setEnergy(energy);
  }

  /**
   * 每帧推进。挂到**那唯一一个** ticker 回调上。
   *
   * ⚠️ **两张遮罩都必须先算**：背景的标量场要采样它们，晚一帧就会看到
   * "卡牌动了、洞没跟上"或者"裂缝晚一拍"。
   *
   * ⚠️ `viewport` 是**世界变换**（`ui/screenHost.ts` 每帧读一次传进来）。
   * 场要铺满整块视口，而"视口有多大"只有世界变换知道 —— 见
   * `Background.setViewport` 和 `shaders.ts` 的文件头。
   */
  update(ticker: Ticker, cards: readonly CardView[] = [], viewport?: Viewport): void {
    if (viewport !== undefined) {
      this.background.setViewport(viewport);
      /**
       * ★ **濒死那两层也要铺满整块视口。**
       *
       * 评审：「红线没有延伸至画面边缘。」根因就是它们按**画布**铺（1280 宽），
       * 而窗口不是 16:10 时视口比画布大 —— 两边那几条黑边上背景是有的、
       * 横带和裂缝却停在画布边。
       *
       * ⚠️ 换算一次就够：`quadOf` 是"视口 → 画布逻辑坐标"的唯一一处实现。
       */
      const quad = quadOf(viewport);
      const rect = { x: quad[0], y: quad[1], w: quad[2], h: quad[3] };
      this.dying.setViewport(rect);
      this.cracks.setViewport(rect);
    }

    this.footprint.update(this.#renderer, cards);
    this.cracks.update(ticker);
    // 裂缝的强度每帧推给标量场（它在淡出时是变化的）
    this.background.setCrack(this.cracks.mask, this.cracks.strength);
    this.background.update(ticker);
    this.dying.update(ticker);
  }

  destroy(): void {
    this.view.destroy({ children: true });
    this.background.destroy();
    this.dying.destroy();
    this.cracks.destroy();
    this.footprint.destroy();
  }
}
